import { useCallback, useEffect, useRef, useState } from 'react';

import {
	CONNECT_APPS_BG_DURATION,
	CONNECT_APPS_ICON_STEP,
	CONNECT_APPS_SLIDE_DURATION,
	END_LINGER,
	HOLD_DURATION,
} from './timing';

/**
 * Linear phase machine for the Connect Apps animation.
 *
 *   idle → bg-fade-in → sliding-out → icon-cycling
 *        → sliding-back → bg-fade-out → done
 *
 * The reverse tail (`sliding-back → bg-fade-out → done`) can also be triggered
 * mid-flight via `exitEarly()` when the user clicks the pill while it is open.
 */
export type ConnectAppsPhase =
	| 'idle'
	| 'bg-fade-in'
	| 'sliding-out'
	| 'icon-cycling'
	| 'sliding-back'
	| 'bg-fade-out'
	| 'done';

/** Pill is dark/elevated. True for every "active" phase except `done`. */
export const isElevated = (phase: ConnectAppsPhase): boolean =>
	phase === 'bg-fade-in' ||
	phase === 'sliding-out' ||
	phase === 'icon-cycling' ||
	phase === 'sliding-back';

/** Label is visible (text slid out). */
export const isTextOut = (phase: ConnectAppsPhase): boolean =>
	phase === 'sliding-out' || phase === 'icon-cycling';

type Step = { atMs: number; apply: () => void };

type Options = {
	/** True when the animation should run (e.g. search bar opens). */
	start: boolean;
	/** Number of icons in the sequence to cycle through. */
	sequenceLength: number;
	/** Called once before each run; return false to suppress this run. */
	canStart: () => boolean;
	/** Called exactly once when a run is starting. */
	onStart: () => void;
};

export type ConnectAppsAnimation = {
	phase: ConnectAppsPhase;
	activeIndex: number;
	/** Cancel forward sequence and play only the reverse tail. */
	exitEarly: () => void;
};

export const useConnectAppsAnimation = ({
	start,
	sequenceLength,
	canStart,
	onStart,
}: Options): ConnectAppsAnimation => {
	const [phase, setPhase] = useState<ConnectAppsPhase>('idle');
	const [activeIndex, setActiveIndex] = useState(0);
	const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const hasStartedRef = useRef(false);

	const cancelTimers = useCallback(() => {
		timersRef.current.forEach(clearTimeout);
		timersRef.current = [];
	}, []);

	const scheduleAfter = useCallback((delayMs: number, fn: () => void) => {
		timersRef.current.push(setTimeout(fn, delayMs));
	}, []);

	const playReverseTail = useCallback(() => {
		setActiveIndex(0);
		setPhase('sliding-back');
		scheduleAfter(CONNECT_APPS_SLIDE_DURATION, () => {
			setPhase('bg-fade-out');
			scheduleAfter(CONNECT_APPS_BG_DURATION, () => setPhase('done'));
		});
	}, [scheduleAfter]);

	const exitEarly = useCallback(() => {
		cancelTimers();
		playReverseTail();
	}, [cancelTimers, playReverseTail]);

	useEffect(() => {
		if (!start) {
			cancelTimers();
			setPhase('idle');
			setActiveIndex(0);
			return;
		}
		if (hasStartedRef.current || !canStart()) {
			return;
		}
		hasStartedRef.current = true;
		onStart();

		// Build the full timeline up-front so forward & reverse share one schedule.
		const cycleStart = CONNECT_APPS_BG_DURATION + CONNECT_APPS_SLIDE_DURATION;
		const cycleDuration = (sequenceLength - 1) * CONNECT_APPS_ICON_STEP + HOLD_DURATION;
		const reverseStart = cycleStart + cycleDuration + END_LINGER;

		const steps: Step[] = [
			{ atMs: 0, apply: () => setPhase('bg-fade-in') },
			{ atMs: CONNECT_APPS_BG_DURATION, apply: () => setPhase('sliding-out') },
			{ atMs: cycleStart, apply: () => setPhase('icon-cycling') },
		];

		// Icon i (1..N-1) becomes active HOLD ms after icon-cycling starts,
		// then every ICON_STEP ms thereafter.
		for (let i = 1; i < sequenceLength; i++) {
			steps.push({
				atMs: cycleStart + HOLD_DURATION + (i - 1) * CONNECT_APPS_ICON_STEP,
				apply: () => setActiveIndex(i),
			});
		}

		steps.push(
			{
				atMs: reverseStart,
				apply: () => {
					setActiveIndex(0);
					setPhase('sliding-back');
				},
			},
			{
				atMs: reverseStart + CONNECT_APPS_SLIDE_DURATION,
				apply: () => setPhase('bg-fade-out'),
			},
			{
				atMs: reverseStart + CONNECT_APPS_SLIDE_DURATION + CONNECT_APPS_BG_DURATION,
				apply: () => setPhase('done'),
			},
		);

		steps.forEach(({ atMs, apply }) => scheduleAfter(atMs, apply));

		return cancelTimers;
	}, [start, sequenceLength, canStart, onStart, cancelTimers, scheduleAfter]);

	return { phase, activeIndex, exitEarly };
};
